import { motion } from 'framer-motion'
import { Check, CheckCheck, AlertCircle, RefreshCw, Download, FileText, FileSpreadsheet, FileImage, File } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import { ChatMessage, MessageStatus } from '@/store/chatStore'
import { NikiAvatarMini } from '@/components/niki/NikiAvatar'
import { formatTimestamp, cn } from '@/utils'

interface MessageBubbleProps {
  message: ChatMessage
  showAvatar?: boolean
  showTimestamp?: boolean
  onRetry?: () => void
}

// Extract YouTube video ID from various URL formats
function extractYouTubeVideoId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/v\/([a-zA-Z0-9_-]{11})/,
  ]
  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match) return match[1]
  }
  return null
}

// Extract video ID from any URL (including embed URLs)
function extractVideoIdFromUrl(url: string): string | null {
  if (!url) return null
  return extractYouTubeVideoId(url)
}

// Check if content has YouTube embed (either iframe or URL)
function hasYouTubeEmbed(content: string): { hasEmbed: boolean; videoId: string | null } {
  // Check for iframe with YouTube src
  const iframeMatch = content.match(/<iframe[^>]*src=["']([^"']*youtube[^"']*)["'][^>]*>/i)
  if (iframeMatch) {
    const videoId = extractYouTubeVideoId(iframeMatch[1])
    if (videoId) return { hasEmbed: true, videoId }
  }

  // Check for YouTube URLs in text
  const urlMatch = content.match(/(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/)
  if (urlMatch) {
    return { hasEmbed: true, videoId: urlMatch[1] }
  }

  return { hasEmbed: false, videoId: null }
}

// Remove iframe tags from content (we'll render them separately)
function removeIframeTags(content: string): string {
  return content.replace(/<iframe[^>]*>[\s\S]*?<\/iframe>/gi, '')
                .replace(/<iframe[^>]*>/gi, '')
}

// YouTube Embed Component
function YouTubeEmbed({ videoId }: { videoId: string }) {
  return (
    <div className="my-3 rounded-xl overflow-hidden aspect-video bg-black/20">
      <iframe
        src={`https://www.youtube.com/embed/${videoId}?autoplay=0&rel=0`}
        className="w-full h-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        title="YouTube video"
      />
    </div>
  )
}

// Chart Embed Component
function ChartEmbed({ url }: { url: string }) {
  const isHtml = url.endsWith('.html')

  return (
    <div className="my-3 rounded-xl overflow-hidden bg-white/5 border border-emerald-500/20">
      {isHtml ? (
        <iframe
          src={url}
          className="w-full"
          style={{ minHeight: '400px' }}
          title="Chart visualization"
        />
      ) : (
        <img
          src={url}
          alt="Chart visualization"
          className="w-full h-auto"
          loading="lazy"
        />
      )}
    </div>
  )
}

// Server Video Embed Component (for Remotion generated videos)
function ServerVideoEmbed({ url, title }: { url: string; title?: string }) {
  return (
    <div className="my-3 rounded-xl overflow-hidden bg-black/30 border border-emerald-500/20">
      <video
        src={url}
        controls
        className="w-full aspect-video"
        poster={url.replace('.mp4', '_thumb.jpg')}
        preload="metadata"
      >
        <source src={url} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
      {title && (
        <div className="px-3 py-2 bg-black/20 text-sm text-white/70">
          {title}
        </div>
      )}
    </div>
  )
}

// Check if a URL is from our server (not YouTube)
function isServerVideoUrl(url: string | undefined): boolean {
  if (!url) return false
  return url.includes('/videos/') || url.includes('masterlyaiclass.com') || url.includes('localhost')
}

// Document Download Component
interface DocumentDownloadProps {
  title: string
  downloadUrl: string
  documentType?: string
  filename?: string
  slideCount?: number
}

function DocumentDownload({ title, downloadUrl, documentType, filename, slideCount }: DocumentDownloadProps) {
  const getIcon = () => {
    switch (documentType) {
      case 'presentation':
        return <FileText className="w-6 h-6" />
      case 'spreadsheet':
        return <FileSpreadsheet className="w-6 h-6" />
      case 'pdf':
        return <FileImage className="w-6 h-6" />
      default:
        return <File className="w-6 h-6" />
    }
  }

  const getFileTypeLabel = () => {
    switch (documentType) {
      case 'presentation':
        return 'PowerPoint Presentation'
      case 'spreadsheet':
        return 'Excel Spreadsheet'
      case 'pdf':
        return 'PDF Document'
      default:
        return 'Document'
    }
  }

  // Build the full download URL
  const fullUrl = downloadUrl.startsWith('http')
    ? downloadUrl
    : `${window.location.origin}${downloadUrl}`

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="my-3 p-4 rounded-xl bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border border-emerald-500/30"
    >
      <div className="flex items-start gap-4">
        {/* File Icon */}
        <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-emerald-500/30 flex items-center justify-center text-emerald-300">
          {getIcon()}
        </div>

        {/* File Info */}
        <div className="flex-1 min-w-0">
          <h4 className="text-white font-medium truncate">{title}</h4>
          <p className="text-sm text-white/60 mt-0.5">
            {getFileTypeLabel()}
            {slideCount && ` • ${slideCount} slides`}
          </p>
          {filename && (
            <p className="text-xs text-white/40 mt-1 truncate">{filename}</p>
          )}
        </div>

        {/* Download Button */}
        <motion.a
          href={fullUrl}
          download={filename || true}
          target="_blank"
          rel="noopener noreferrer"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white font-medium transition-colors"
        >
          <Download className="w-4 h-4" />
          <span>Download</span>
        </motion.a>
      </div>
    </motion.div>
  )
}

// Extract chart URL from content or metadata
function extractChartUrl(content: string, metadata?: Record<string, unknown>): string | null {
  // Check metadata first
  if (metadata?.image_url && typeof metadata.image_url === 'string') {
    return metadata.image_url.startsWith('/')
      ? `${window.location.origin}${metadata.image_url}`
      : metadata.image_url
  }

  // Check content for chart URLs
  const chartMatch = content.match(/\/static\/charts\/[^\s\)\"']+\.(html|png|jpg)/i)
  if (chartMatch) {
    return `${window.location.origin}${chartMatch[0]}`
  }

  return null
}

export function MessageBubble({
  message,
  showAvatar = true,
  showTimestamp = true,
  onRetry,
}: MessageBubbleProps) {
  const isUser = message.role === 'user'

  // Check for server-hosted video (Remotion generated)
  const serverVideoUrl = message.metadata?.video_url as string | undefined
  const hasServerVideo = !isUser && isServerVideoUrl(serverVideoUrl)

  // Check for YouTube embed - only if NOT a server video
  const youtubeEmbedUrl = message.metadata?.embed_url as string | undefined
  const youtubeVideoId = !hasServerVideo && youtubeEmbedUrl
    ? extractVideoIdFromUrl(youtubeEmbedUrl)
    : null

  // Also check content for YouTube URLs
  const { hasEmbed: hasEmbedInContent, videoId: videoIdFromContent } = !isUser && !hasServerVideo
    ? hasYouTubeEmbed(message.content)
    : { hasEmbed: false, videoId: null }

  const hasYouTubeVideo = !!(youtubeVideoId || hasEmbedInContent)
  const youtubeId = youtubeVideoId || videoIdFromContent

  // Check for chart embed
  const chartUrl = !isUser ? extractChartUrl(message.content, message.metadata as Record<string, unknown>) : null

  // Check for document download
  const documentMetadata = message.metadata as Record<string, unknown> | undefined
  const hasDocumentDownload = !isUser && documentMetadata?.type === 'document_download'

  // Clean content for markdown (remove raw iframes)
  const cleanContent = !isUser ? removeIframeTags(message.content) : message.content

  return (
    <motion.div
      initial={{ opacity: 0, x: isUser ? 30 : -30, y: 10 }}
      animate={{ opacity: 1, x: 0, y: 0 }}
      transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
      className={cn(
        'flex gap-3 mb-4',
        isUser ? 'flex-row-reverse' : ''
      )}
    >
      {/* Avatar */}
      {!isUser && (
        <div className={cn('flex-shrink-0 self-end', !showAvatar && 'invisible')}>
          <NikiAvatarMini state="idle" />
        </div>
      )}

      {/* Bubble Container */}
      <div className={cn('flex flex-col max-w-[85%]', isUser ? 'items-end' : 'items-start')}>
        {/* Message Bubble */}
        <motion.div
          whileHover={{ scale: 1.005 }}
          className={cn(
            'relative px-4 py-3 max-w-full',
            isUser ? 'message-user' : 'message-niki'
          )}
        >
          {/* Message content - render markdown for assistant messages */}
          {isUser ? (
            <p className="text-[15px] leading-relaxed text-white whitespace-pre-wrap break-words">
              {message.content}
            </p>
          ) : (
            <div className="markdown-content">
              {/* Server Video Embed (Remotion generated) */}
              {hasServerVideo && serverVideoUrl && (
                <ServerVideoEmbed
                  url={serverVideoUrl}
                  title={message.metadata?.title as string}
                />
              )}

              {/* YouTube Embed */}
              {hasYouTubeVideo && youtubeId && <YouTubeEmbed videoId={youtubeId} />}

              {/* Chart Embed */}
              {chartUrl && <ChartEmbed url={chartUrl} />}

              {/* Document Download */}
              {hasDocumentDownload && documentMetadata && (
                <DocumentDownload
                  title={documentMetadata.title as string || 'Document'}
                  downloadUrl={documentMetadata.download_url as string || documentMetadata.file_url as string || ''}
                  documentType={documentMetadata.document_type as string}
                  filename={documentMetadata.filename as string}
                  slideCount={documentMetadata.slide_count as number}
                />
              )}

              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={{
                  // Custom link handling - bright green for visibility on dark
                  a: ({ href, children }) => (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-emerald-400 hover:text-emerald-300 underline transition-colors"
                    >
                      {children}
                    </a>
                  ),
                  // Tables
                  table: ({ children }) => (
                    <div className="my-3 overflow-x-auto rounded-lg border border-emerald-500/30">
                      <table className="w-full text-sm border-collapse">
                        {children}
                      </table>
                    </div>
                  ),
                  thead: ({ children }) => (
                    <thead className="bg-emerald-500/20 text-emerald-300">
                      {children}
                    </thead>
                  ),
                  tbody: ({ children }) => (
                    <tbody className="divide-y divide-emerald-500/20">
                      {children}
                    </tbody>
                  ),
                  tr: ({ children }) => (
                    <tr className="hover:bg-emerald-500/10 transition-colors">
                      {children}
                    </tr>
                  ),
                  th: ({ children }) => (
                    <th className="px-3 py-2 text-left font-semibold border-b border-emerald-500/30">
                      {children}
                    </th>
                  ),
                  td: ({ children }) => (
                    <td className="px-3 py-2 border-b border-emerald-500/20 text-white/90">
                      {children}
                    </td>
                  ),
                  // Headers - bright green
                  h1: ({ children }) => (
                    <h1 className="text-xl font-bold mb-3 mt-4 text-emerald-300">
                      {children}
                    </h1>
                  ),
                  h2: ({ children }) => (
                    <h2 className="text-lg font-semibold mb-2 mt-3 text-emerald-400">
                      {children}
                    </h2>
                  ),
                  h3: ({ children }) => (
                    <h3 className="text-base font-semibold mb-2 mt-2 text-emerald-400">
                      {children}
                    </h3>
                  ),
                  // Lists
                  ul: ({ children }) => (
                    <ul className="list-disc list-inside space-y-1 my-2 ml-2 text-white/90">
                      {children}
                    </ul>
                  ),
                  ol: ({ children }) => (
                    <ol className="list-decimal list-inside space-y-1 my-2 ml-2 text-white/90">
                      {children}
                    </ol>
                  ),
                  li: ({ children }) => (
                    <li className="text-white/90">
                      {children}
                    </li>
                  ),
                  // Paragraphs
                  p: ({ children }) => (
                    <p className="text-[15px] leading-relaxed mb-2 last:mb-0 text-white/90">
                      {children}
                    </p>
                  ),
                  // Strong/Bold
                  strong: ({ children }) => (
                    <strong className="font-semibold text-emerald-300">
                      {children}
                    </strong>
                  ),
                  // Emphasis/Italic
                  em: ({ children }) => (
                    <em className="italic text-white/80">
                      {children}
                    </em>
                  ),
                  // Horizontal rule
                  hr: () => (
                    <hr className="my-3 border-emerald-500/30" />
                  ),
                  // Blockquote - green accent
                  blockquote: ({ children }) => (
                    <blockquote className="pl-4 my-3 border-l-4 border-emerald-500 text-white/70 italic">
                      {children}
                    </blockquote>
                  ),
                  // Code blocks
                  code: ({ className, children, ...props }) => {
                    const isInline = !className
                    return isInline ? (
                      <code className="px-1.5 py-0.5 rounded text-sm font-mono bg-emerald-500/20 text-emerald-300" {...props}>
                        {children}
                      </code>
                    ) : (
                      <code className="block p-3 my-2 rounded-lg text-sm font-mono overflow-x-auto bg-black/40 text-emerald-300" {...props}>
                        {children}
                      </code>
                    )
                  },
                  pre: ({ children }) => (
                    <pre className="my-2 rounded-lg overflow-hidden">
                      {children}
                    </pre>
                  ),
                }}
              >
                {cleanContent}
              </ReactMarkdown>
            </div>
          )}

          {/* Error state with retry */}
          {message.status === 'error' && onRetry && (
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              onClick={onRetry}
              className="mt-2 flex items-center gap-2 text-danger hover:text-danger-light transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              <span className="text-xs">Failed to send. Tap to retry.</span>
            </motion.button>
          )}

          {/* Subtle glow for Niki messages */}
          {!isUser && (
            <div
              className="absolute inset-0 rounded-2xl rounded-bl-sm pointer-events-none"
              style={{
                background: 'radial-gradient(ellipse at center, rgba(16, 185, 129, 0.03) 0%, transparent 70%)',
              }}
            />
          )}
        </motion.div>

        {/* Timestamp and Status */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className={cn(
            'flex items-center gap-2 mt-1 px-1',
            isUser ? 'flex-row-reverse' : ''
          )}
        >
          {showTimestamp && (
            <span className="text-[11px] text-white/40">
              {formatTimestamp(message.timestamp)}
            </span>
          )}
          {isUser && <StatusIndicator status={message.status} />}
        </motion.div>
      </div>
    </motion.div>
  )
}

function StatusIndicator({ status }: { status: MessageStatus }) {
  switch (status) {
    case 'sending':
      return (
        <motion.div
          className="w-4 h-4"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        >
          <div className="w-3 h-3 border-2 border-white/30 border-t-white/60 rounded-full" />
        </motion.div>
      )
    case 'sent':
      return <Check className="w-3.5 h-3.5 text-white/40" />
    case 'delivered':
      return <CheckCheck className="w-3.5 h-3.5 text-emerald-400" />
    case 'error':
      return <AlertCircle className="w-3.5 h-3.5 text-danger" />
    default:
      return null
  }
}

// Grouped message variant for consecutive messages from the same sender
export function MessageGroup({ messages, onRetry }: { messages: ChatMessage[]; onRetry?: (id: string) => void }) {
  return (
    <div className="space-y-1">
      {messages.map((message, index) => (
        <MessageBubble
          key={message.id}
          message={message}
          showAvatar={index === messages.length - 1}
          showTimestamp={index === messages.length - 1}
          onRetry={onRetry ? () => onRetry(message.id) : undefined}
        />
      ))}
    </div>
  )
}
